import {
  createContext,
  useContext,
  useMemo,
  useState,
  type FC,
  type PropsWithChildren,
} from "react";
import { createPortal } from "react-dom";

interface PageHeaderFilterBarSlotContextValue {
  element: HTMLDivElement | null;
  setElement: (element: HTMLDivElement | null) => void;
}

const PageHeaderFilterBarSlotContext =
  createContext<PageHeaderFilterBarSlotContextValue | null>(null);

function usePageHeaderFilterBarSlotContext(
  componentName: string,
): PageHeaderFilterBarSlotContextValue {
  const context = useContext(PageHeaderFilterBarSlotContext);
  if (!context) {
    throw new Error(
      `${componentName} requires a PageHeaderFilterBarSlotProvider ancestor.`,
    );
  }
  return context;
}

/**
 * Lets routed tab content render its filter bar into the page's `PageHeader`,
 * so tabbed pages use the same `filterBar` slot as every other page while the
 * bar's state stays owned by the active tab. Wrap the page that owns the
 * header, pass `<PageHeaderFilterBarSlot />` as the header's `filterBar`, and
 * have tabs render their bar inside `<PageHeaderFilterBarPortal>`.
 */
export const PageHeaderFilterBarSlotProvider: FC<PropsWithChildren> = ({
  children,
}) => {
  const [element, setElement] = useState<HTMLDivElement | null>(null);
  const value = useMemo(() => ({ element, setElement }), [element]);
  return (
    <PageHeaderFilterBarSlotContext.Provider value={value}>
      {children}
    </PageHeaderFilterBarSlotContext.Provider>
  );
};

/** The slot target; pass as the `filterBar` of the page's `PageHeader`. */
export const PageHeaderFilterBarSlot: FC = () => {
  const { setElement } = usePageHeaderFilterBarSlotContext(
    "PageHeaderFilterBarSlot",
  );
  return <div ref={setElement} />;
};

/** Renders a tab's filter bar into the ancestor page header's slot. */
export const PageHeaderFilterBarPortal: FC<PropsWithChildren> = ({
  children,
}) => {
  const { element } = usePageHeaderFilterBarSlotContext(
    "PageHeaderFilterBarPortal",
  );
  return element ? createPortal(children, element) : null;
};
